import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faIdCard, faPlus, faEdit, faTrash, faSearch,
    faSave, faTimes, faSync, faToggleOn, faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE   = process.env.REACT_APP_API_URL || 'http://localhost:5000/';
const EMPTY_PROF = { ProfileName: '', ProfileDesc: '', ProfileActive: 1 };

/**
 * ProfileABM — CRUD for tf_profile table.
 * Profiles are roles that group permission sets for admin users.
 */
const ProfileABM = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState('');
    const [modal,    setModal]    = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [form,     setForm]     = useState(EMPTY_PROF);
    const [editId,   setEditId]   = useState(null);

    useEffect(() => { fetchProfiles(); }, []);

    const fetchProfiles = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/profiles', {}, r => {
            if (r.success) setProfiles(r.data || []);
            else Notify({ type: 'fail', text: 'Error al cargar perfiles' });
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return profiles.filter(p => !q || p.ProfileName?.toLowerCase().includes(q));
    }, [profiles, search]);

    const openNew  = () => { setForm({ ...EMPTY_PROF }); setEditId(null); setModal(true); };
    const openEdit = (p) => {
        setForm({ ProfileName: p.ProfileName, ProfileDesc: p.ProfileDesc || '', ProfileActive: p.ProfileActive });
        setEditId(p.ProfileId);
        setModal(true);
    };
    const closeModal = () => { if (!saving) { setModal(false); setForm(EMPTY_PROF); setEditId(null); } };

    const handleSave = () => {
        if (!form.ProfileName) { Notify({ type: 'fail', text: 'El nombre es obligatorio' }); return; }
        setSaving(true);
        const done = (r) => {
            if (r.success) {
                Notify({ type: 'success', text: editId ? 'Perfil actualizado' : 'Perfil creado' });
                fetchProfiles();
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al guardar' });
            }
            setSaving(false);
        };
        const fail = () => setSaving(false);
        if (editId) {
            request.Put(API_BASE + `superadmin/profiles/${editId}`, form, done, 'json', fail);
        } else {
            request.Post(API_BASE + 'superadmin/profiles', form, done, 'json', fail);
        }
    };

    const handleDelete = (p) => {
        setDeleting(p.ProfileId);
        request.Delete(API_BASE + `superadmin/profiles/${p.ProfileId}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Perfil eliminado' });
                setProfiles(prev => prev.filter(x => x.ProfileId !== p.ProfileId));
            } else {
                Notify({ type: 'fail', text: r.msg || 'No se puede eliminar (puede estar asignado a admins)' });
            }
            setDeleting(null);
        }, 'json', () => setDeleting(null));
    };

    const toggleActive = (p) => {
        request.Put(API_BASE + `superadmin/profiles/${p.ProfileId}`, { ...p, ProfileActive: p.ProfileActive ? 0 : 1 }, r => {
            if (r.success) {
                setProfiles(prev => prev.map(x => x.ProfileId === p.ProfileId
                    ? { ...x, ProfileActive: p.ProfileActive ? 0 : 1 } : x
                ));
            }
        });
    };

    const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="sa-search-bar" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color: '#475569' }} />
                    <input placeholder="Buscar perfil..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <button className="sa-btn sa-btn-ghost" onClick={fetchProfiles} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                    <button className="sa-btn sa-btn-primary" onClick={openNew}>
                        <FontAwesomeIcon icon={faPlus} /> Nuevo Perfil
                    </button>
                </div>
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Nombre del Perfil</th>
                                    <th>Descripción</th>
                                    <th>Módulos Asignados</th>
                                    <th>Admins con este Perfil</th>
                                    <th>Activo</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={6} className="text-center py-4 text-muted">Sin perfiles registrados</td></tr>
                                )}
                                {filtered.map(p => (
                                    <tr key={p.ProfileId}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: 10,
                                                    background: 'rgba(168,85,247,0.12)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'var(--sa-primary)', fontSize: '0.8rem'
                                                }}>
                                                    <FontAwesomeIcon icon={faIdCard} />
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{p.ProfileName}</span>
                                            </div>
                                        </td>
                                        <td style={{ color: '#64748b', fontSize: '0.85rem', maxWidth: 240 }}>
                                            {p.ProfileDesc || <em style={{ opacity: 0.4 }}>Sin descripción</em>}
                                        </td>
                                        <td>
                                            <span className="sa-badge sa-badge-premium">{p.ModuleCount ?? 0} módulos</span>
                                        </td>
                                        <td>
                                            <span className="sa-badge sa-badge-trial">{p.AdminCount ?? 0} admins</span>
                                        </td>
                                        <td>
                                            <button
                                                className={`sa-btn sa-btn-sm ${p.ProfileActive ? 'sa-btn-success' : 'sa-btn-ghost'}`}
                                                onClick={() => toggleActive(p)}
                                            >
                                                <FontAwesomeIcon icon={p.ProfileActive ? faToggleOn : faToggleOff} />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => openEdit(p)}>
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={() => handleDelete(p)} disabled={deleting === p.ProfileId}>
                                                    {deleting === p.ProfileId ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faTrash} />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal show={modal} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, color: '#f8fafc', fontSize: '1.1rem' }}>
                        <FontAwesomeIcon icon={faIdCard} style={{ color: 'var(--sa-primary)', marginRight: 8 }} />
                        {editId ? 'Editar Perfil' : 'Nuevo Perfil'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-3">
                        <div>
                            <label className="sa-form-label">Nombre del Perfil *</label>
                            <input className="sa-input" placeholder="ej: AdminEmpresa" value={form.ProfileName}
                                onChange={e => f('ProfileName', e.target.value)} />
                        </div>
                        <div>
                            <label className="sa-form-label">Descripción</label>
                            <textarea className="sa-input" rows={3} placeholder="Descripción del perfil y sus permisos..."
                                value={form.ProfileDesc} onChange={e => f('ProfileDesc', e.target.value)}
                                style={{ resize: 'none' }} />
                        </div>
                        <div>
                            <label className="sa-form-label">Estado</label>
                            <select className="sa-input" value={form.ProfileActive} onChange={e => f('ProfileActive', parseInt(e.target.value))}>
                                <option value={1}>Activo</option>
                                <option value={0}>Inactivo</option>
                            </select>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /> Cancelar</button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar</>}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ProfileABM;
